import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { contactSchema } from "../../schemas/contactSchema";
import { FileText } from "lucide-react";
import LoadingButton from "../../components/LoadingButton";
import { useState } from "react";
import { useFileUpload } from "../../hooks/useFileUplode";

export default function ContactForm({
  mode,
  defaultValues = {},
  onSubmit,
  onCancel,
  submissionLoading = false,
  CMSData = {},
}) {
  const [uploadedFiles, setUploadedFiles] = useState(defaultValues.file_uploaded || []);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const { fileUpload } = useFileUpload();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(contactSchema),
    defaultValues: {
      reason: defaultValues.reason || "",
      description: defaultValues.description || "",
      file_uploaded: defaultValues.file_uploaded || [],
      is_resolved: defaultValues.is_resolved || false,
    },
  });
  const description = watch("description");
  const reason = watch("reason");
  const isResolved = watch("is_resolved");

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("document", file);
        const response = await fileUpload(formData);
        if (response && response.url) {
          const newFile = { url: response.url, name: file.name };
          const updatedFiles = [...uploadedFiles, newFile];
          setUploadedFiles(updatedFiles);
          setValue("file_uploaded", updatedFiles);
        }
      }
    } catch (error) {
      setUploadError(error.message || "File upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedFiles);
    setValue("file_uploaded", updatedFiles);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Reason */}
      <div>
        <label className="block text-sm font-medium mb-1">Reason</label>
        <input
          {...register("reason")}
          readOnly={mode === "view"}
          placeholder="Enter reason"
          className="pl-3 pr-3 py-2 border rounded w-full"
        />
        {errors.reason && (
          <p className="text-red-500 text-xs mt-1">{errors.reason.message}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <div className="relative">
          <textarea
            {...register("description")}
            readOnly={mode === "view"}
            placeholder="Enter description"
            className="pl-3 pr-3 py-2 border rounded w-full min-h-[100px]"
          />
        </div>
        {errors.description && (
          <p className="text-red-500 text-xs mt-1">{errors.description.message}</p>
        )}
      </div>

      {/* File Upload */}
      {/* <div>
        {mode !== "view" && (
          <label className="block text-sm font-medium mb-1">Upload Files</label>
        )}<label className="block text-sm font-medium mb-1">Uploaded Files</label>
        <Controller
          name="file_uploaded"
          control={control}
          render={({ field }) => (
            <>
              {mode !== "view" && (
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark"
                />
              )}
              {uploading && <p className="text-blue-500 text-xs mt-1">Uploading...</p>}
              {uploadError && <p className="text-red-500 text-xs mt-1">{uploadError}</p>}
<ul className={`mt-2 ${mode === "view" ? "flex space-x-4" : ""}`}>
{uploadedFiles.map((file, index) => (
  <li key={index} className={`flex items-center ${mode === "view" ? "" : "justify-between"} py-1`}>
    {mode === "view" ? (
      <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-blue-600">
        <FileText className="w-5 h-5 cursor-pointer" />
      </a>
    ) : (
      <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
        {file.name || file.url}
      </a>
    )}
    {mode !== "view" && (
      <button
        type="button"
        onClick={() => handleRemoveFile(index)}
        className="text-red-500 ml-2"
      >
        Remove
      </button>
    )}
  </li>
))}
</ul>
            </>
          )}
        />
      </div> */}

      {/* Is Resolved */}
      {mode !== "view" && (
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            {...register("is_resolved")}
            checked={isResolved}
            onChange={(e) => setValue("is_resolved", e.target.checked)}
            id="is_resolved"
          />
          <label htmlFor="is_resolved" className="text-sm font-medium">
            Is Resolved
          </label>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border rounded text-gray-700"
        >
          {mode === "view" ? "Close" : "Cancel"}
        </button>
        {mode !== "view" && (
          <button
            type="submit"
            disabled={submissionLoading}
            className="px-4 py-2 bg-primary text-white rounded flex items-center justify-center gap-2"
          >
            {submissionLoading ? (
              <LoadingButton />
            ) : (
              <>{mode === "edit" ? "Save Changes" : "Create Page"}</>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
